<script lang="ts" setup>
  import { Drawer, EventDrawer, BaseButton, Text, ShareButton, Accordion } from '@bt/design-system'
  import { storeToRefs } from 'pinia'
  import { head } from 'lodash-es'
  import type { InvestorEvent } from '@/types/Storyblok/General/EventCard'
  import { useAppStore } from '@/stores'
  import { useFetchInvestorEvent, useFetchPaginationEvent } from '@/composables/useFetchInvestorEvent'

  const { fetchInvestorEvent } = useFetchInvestorEvent()
  const { fetchPaginationEvent } = useFetchPaginationEvent()

  const appStore = useAppStore()
  const { cmsPath, cmsPathParams, loading } = storeToRefs(appStore)
  const { locale, t } = useI18n()
  const { query, path } = useRoute()
  const { push: pushRouter } = useRouter()
  const isDrawerVisible = ref(false)
  const activeEvent = ref({ originalDate: '', slug: '' })
  const activeEventDrawerData = ref({ date: '', location: '', title: '', text: '' })
  const eventData = ref([])
  const isPreviousBtnDisabled = ref(false)
  const isNextBtnDisabled = ref(false)

  const props = withDefaults(
    defineProps<{
      blok: {
        [key: string]: InvestorEvent[]
      }
      showAccordion?: boolean
      showSlider?: boolean
    }>(),
    {
      showAccordion: false,
      showSlider: false
    }
  )

  const hasEventCardDataLoadedOnMounted = useState('hasEventCardDataLoadedOnMounted', () => false)
  const triggerComponent = useState('triggerComponent', () => '')

  onMounted(() => {
    if (query?.event && !hasEventCardDataLoadedOnMounted.value) {
      hasEventCardDataLoadedOnMounted.value = true
      openEvent(query.event.toString())
    }
  })

  const currentComponent = computed(() => (props.showAccordion ? 'accordion' : 'card'))

  const router = useRoute()
  watch(
    () => router.query.event,
    (slug) => {
      if (slug && (currentComponent.value === triggerComponent.value || !triggerComponent.value)) {
        openEvent(slug.toString())
      } else if (!slug) {
        isDrawerVisible.value = false
      }
    }
  )

  const openEvent = async (slug: string) => {
    await fetchInvestorEvent(locale.value, slug).then((response) => singleInvestorEventFetchCallback([response]))
    await getInvestorEventData(activeEvent.value.originalDate, true, true)
    await getInvestorEventData(activeEvent.value.originalDate, false, true)
  }

  const getInvestorEventData = async (date: string, isFutureEvent: boolean, skipDataAssigment?: boolean) => {
    await fetchPaginationEvent(locale.value, date, isFutureEvent).then((re) =>
      skipDataAssigment
        ? checkIfStepButtonIsAvailable(re, isFutureEvent)
        : singleInvestorEventFetchCallback(re, isFutureEvent)
    )
  }

  const checkIfStepButtonIsAvailable = (re: any, isFutureEvent: boolean) => {
    const items = re.filter((item: any) => item.content.date)

    if (items.length < 1 && typeof isFutureEvent === 'boolean') {
      if (isFutureEvent) {
        isNextBtnDisabled.value = true
      } else {
        isPreviousBtnDisabled.value = true
      }
    }
  }

  const singleInvestorEventFetchCallback = (re: InvestorEvent[], isFutureEvent?: boolean) => {
    const areItemsValid = re.every((item: any) => item.content.date)

    if ((re.length < 2 || !areItemsValid) && typeof isFutureEvent === 'boolean') {
      if (isFutureEvent) {
        isNextBtnDisabled.value = true
      } else {
        isPreviousBtnDisabled.value = true
      }
    } else {
      isNextBtnDisabled.value = false
      isPreviousBtnDisabled.value = false
    }

    const data = head(re)
    if (!data?.content?.date) {
      loading.value = false

      return
    }

    activeEvent.value = {
      originalDate: data.content.date,
      slug: data.slug
    }

    activeEventDrawerData.value.date = data.content.date

    eventData.value = data.content.body
    isDrawerVisible.value = true

    triggerComponent.value = currentComponent.value

    pushRouter({
      path: t('investor-events.slug'),
      query: {
        event: data.slug
      }
    })
  }

  const loadEvent = (isFutureEvent: boolean) => {
    loading.value = true

    if (activeEvent.value.originalDate) {
      getInvestorEventData(activeEvent.value.originalDate, isFutureEvent, false)
    }

    document.querySelector('.drawer-container')?.scrollTo(0, 0)
  }

  const closeEvent = () => {
    hasEventCardDataLoadedOnMounted.value = false

    isDrawerVisible.value = false
    pushRouter({
      path
    })

    if (cmsPathParams) {
      appStore.setCmsPath(path, {})
    }
  }

  const runtimeConfig = useRuntimeConfig()

  const fullInvestorEventUrl = computed(
    () => `${runtimeConfig.public.baseUrl}/${locale.value}/${cmsPath.value}?event=${activeEvent.value.slug}`
  )
  const eventTitle = computed(() => {
    if (!eventData.value.length) {
      return ''
    }

    const event = eventData.value[0] as Object

    if (typeof event === 'object' && 'text' in event) {
      return String(event.text)
    }
  })

  const bindings = computed(() => {
    if (!props.showAccordion) {
      return {}
    }

    return {
      verticalDensity: 'default',
      horizontalDensity: 'default',
      titleType: 'h5',
      color: 'primary',
      contentDensity: 'none',
      class: 'events-cards__accordion border-b border-divider last:border-t'
    }
  })

  onBeforeUnmount(() => {
    clearNuxtState('hasEventCardDataLoadedOnMounted')
    clearNuxtState('triggerComponent')
  })
</script>
<template>
  <template v-if="showSlider">
    <EventSlider
      :events="blok"
      @open="openEvent"
    />
  </template>
  <template v-else>
    <div class="cards-wrapper">
      <component
        :is="showAccordion ? Accordion : 'div'"
        v-for="(events, year) in blok"
        :key="year"
        :title="String(year)"
        v-bind="bindings"
      >
        <div class="flex flex-col gap-4">
          <EventCard
            v-for="card in events"
            :key="card.content.date + card.content.eventType"
            class="shadow lg:!w-full"
            :event-type="card.content.eventType ?? ''"
            :card-text="card.content.cardText ?? ''"
            :card-sub-text="card.content.cardSubText ?? ''"
            :date="card.content.date"
            :has-video-recording="card.content.hasVideoRecording"
            @click="openEvent(card.slug)"
          />
        </div>
      </component>
      <Drawer
        v-model="isDrawerVisible"
        rounded
        backdrop-color="light"
        side="right"
        @update:model-value="closeEvent"
      >
        <EventDrawer
          :data="activeEventDrawerData"
          @close-drawer="closeEvent"
        >
          <template #content>
            <div class="px-4 lg:!px-12">
              <template
                v-for="block in eventData"
                :key="block._uid"
              >
                <StoryblokComponent :blok="block" />
              </template>
            </div>
          </template>
          <template #share>
            <div class="flex items-center gap-4">
              <Text
                :text="`${$t('investor-events.share-event')}:`"
                type="subtitle1"
                class="mr-2"
              />
              <ShareButton :item="{ key: 'twitter', url: fullInvestorEventUrl, text: eventTitle }" />
              <ShareButton :item="{ key: 'facebook', url: fullInvestorEventUrl, text: eventTitle }" />
              <ShareButton :item="{ key: 'linkedin', url: fullInvestorEventUrl }" />
            </div>
          </template>
          <template #actions="{ className }">
            <BaseButton
              :class="className"
              color="ghost"
              :title="$t('investor-events.load-previous')"
              :disabled="isPreviousBtnDisabled"
              @click="loadEvent(false)"
            />
            <BaseButton
              :class="className"
              color="ghost"
              :title="$t('investor-events.load-next')"
              :disabled="isNextBtnDisabled"
              @click="loadEvent(true)"
            />
          </template>
        </EventDrawer>
      </Drawer>
    </div>
  </template>
</template>
<style lang="scss" scoped>
  :deep {
    .drawer-content {
      .video-banner {
        margin-bottom: 2rem;
      }
    }
  }
</style>
